import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Icon from "components/ShellV2/Icon";

// Default Material Symbol shown in the head tile when a screen omits its own.
const FALLBACK_ICON = "help";

function HelpFloatingButtonV2({ helpScreen }) {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation("help");

  // Close on Escape while the modal is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const base = `screens.${helpScreen}`;
  const title = t(`${base}.title`, { defaultValue: "" });
  const sections = t(`${base}.sections`, { returnObjects: true, defaultValue: [] });

  // No content for this screen -> render nothing (matches the design).
  if (!title || !Array.isArray(sections)) return null;

  const icon = t(`${base}.icon`, { defaultValue: FALLBACK_ICON });
  const intro = t(`${base}.intro`, { defaultValue: "" });

  return (
    <>
      <button
        type="button"
        className="rec-help-fab"
        title={t("help_titles.help", { defaultValue: "Ajuda" })}
        aria-label={t("help_titles.help", { defaultValue: "Ajuda" })}
        onClick={() => setOpen(true)}
      >
        <Icon name="question_mark" />
      </button>

      {open && (
        // Backdrop click-to-close; keyboard users close via the Escape handler
        // above, which jsx-a11y cannot associate with this node.
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
        <div
          className="rec-modal-scrim"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="rec-modal rec-help-modal" role="dialog" aria-modal="true">
            <div className="rec-help-head">
              <div className="rec-help-head-ic">
                <Icon name={icon} fill />
              </div>
              <div className="flex-1 min-w-0">
                <div className="rec-help-kicker">
                  {t("help_titles.help", { defaultValue: "Ajuda" })}
                </div>
                <h3>{title}</h3>
              </div>
              <button
                type="button"
                className="rec-icon-btn"
                aria-label={t("help_titles.close", { defaultValue: "Fechar" })}
                onClick={() => setOpen(false)}
              >
                <Icon name="close" />
              </button>
            </div>
            <div className="rec-help-body">
              {intro && <p className="rec-help-intro">{intro}</p>}
              {sections.length > 0 && (
                <div className="rec-help-list">
                  {sections.map((s) => (
                    <div key={s.subtitle} className="rec-help-item">
                      <div className="rec-help-item-dot">
                        <Icon name="check" />
                      </div>
                      <div>
                        <b>{s.subtitle}</b>
                        <p>{s.body}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

HelpFloatingButtonV2.propTypes = {
  helpScreen: PropTypes.string.isRequired,
};

export default HelpFloatingButtonV2;
